import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

// Lightweight, headless Autocomplete with a MUI-compatible surface API for current usages
// Supported props: value, onChange, options, getOptionLabel, isOptionEqualToValue,
// renderInput, size, disableClearable, className, sx, name, placeholder, disablePortal,
// multiple, limitTags, disableCloseOnSelect, renderOption

const defaultGetOptionLabel = (option) => {
  if (option == null) return "";
  if (typeof option === "string") return option;
  if (typeof option.label === "string") return option.label;
  return String(option);
};

const defaultIsEqual = (opt, val) => opt === val;

function coerceStyleFromSx(sx) {
  if (!sx || typeof sx !== "object") return undefined;
  const style = {};
  // Only map top-level CSS-like props (e.g., width, height, margin, etc.)
  for (const [key, value] of Object.entries(sx)) {
    if (typeof value === "object") continue; // ignore nested selectors (MUI style)
    style[key] = value;
  }
  return style;
}

const HodAutocomplete = ({
    value = null,
    onChange,
    options = [],
    getOptionLabel = defaultGetOptionLabel,
    isOptionEqualToValue = defaultIsEqual,
    renderInput,
    size = "medium",
    disableClearable = false,
    className = "",
    sx,
    name,
    placeholder,
    disablePortal = true,
    disabled = false,
    multiple = false,
    limitTags = 1,
    disableCloseOnSelect = false,
    renderOption,
    listboxProps = {},
    ...other
}) => {
    const containerRef = useRef(null);
    const anchorRef = useRef(null);
    const listboxRef = useRef(null);
    const inputRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [inputValue, setInputValue] = useState("");
    // When true, ignore query filtering and show all options on focus/click
    const [showAllOnFocus, setShowAllOnFocus] = useState(false);

    const computeMultiDisplay = useCallback(
        (vals) => {
            const arr = Array.isArray(vals) ? vals : [];
            const labels = arr.map((v) => getOptionLabel(v)).filter(Boolean);
            if (!labels.length) return "";
            const head = labels.slice(0, Math.max(0, limitTags));
            const rest = labels.length - head.length;
            return rest > 0
                ? `${head.join(", ")} +${rest} more`
                : head.join(", ");
        },
        [getOptionLabel, limitTags]
    );

    // Keep inputValue in sync with selected value when menu closed
    useEffect(() => {
        if (!open) {
            if (multiple) {
                setInputValue(computeMultiDisplay(value));
            } else {
                setInputValue(value ? getOptionLabel(value) : "");
            }
            setShowAllOnFocus(false);
        }
    }, [value, open, getOptionLabel, multiple, computeMultiDisplay]);

    const filteredOptions = useMemo(() => {
        if (showAllOnFocus) return options;
        const q = (inputValue || "").toLowerCase();
        if (!q) return options;
        return options.filter((opt) =>
            (getOptionLabel(opt) || "").toLowerCase().includes(q)
        );
    }, [options, inputValue, getOptionLabel, showAllOnFocus]);

    useEffect(() => {
        // Set initial highlight to selected value or first item when opening
        if (open) {
            const idx = value
                ? filteredOptions.findIndex((o) =>
                      isOptionEqualToValue(o, value)
                  )
                : -1;
            setHighlightedIndex(
                idx >= 0 ? idx : filteredOptions.length ? 0 : -1
            );
        }
    }, [open, value, filteredOptions, isOptionEqualToValue]);

    // Close on outside click
    useEffect(() => {
        const handleDocClick = (e) => {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleDocClick);
        return () => document.removeEventListener("mousedown", handleDocClick);
    }, []);

    const selectOption = useCallback(
        (event, option) => {
            if (disabled) return;
            if (!onChange) return;

            if (multiple) {
                const current = Array.isArray(value) ? value.slice() : [];
                const exists =
                    current.findIndex((v) =>
                        isOptionEqualToValue(v, option)
                    ) !== -1;
                let next;
                if (exists) {
                    next = current.filter(
                        (v) => !isOptionEqualToValue(v, option)
                    );
                } else {
                    next = current.concat(option);
                }
                onChange(event, next);
                setInputValue(computeMultiDisplay(next));
                if (!disableCloseOnSelect) setOpen(false);
                else setOpen(true);
            } else {
                onChange(event, option);
                setOpen(false);
                setInputValue(option ? getOptionLabel(option) : "");
            }
        },
        [
            onChange,
            getOptionLabel,
            disabled,
            multiple,
            value,
            isOptionEqualToValue,
            computeMultiDisplay,
            disableCloseOnSelect,
        ]
    );

    const handleInputChange = (e) => {
        if (disabled) return;
        setShowAllOnFocus(false);
        setInputValue(e.target.value);
        if (!open) setOpen(true);
    };

    const handleKeyDown = (e) => {
        if (disabled) return;
        if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            setOpen(true);
            return;
        }
        if (!open) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((i) =>
                i + 1 < filteredOptions.length ? i + 1 : 0
            );
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((i) =>
                i > 0 ? i - 1 : filteredOptions.length - 1
            );
        } else if (e.key === "Enter") {
            e.preventDefault();
            const opt = filteredOptions[highlightedIndex];
            if (opt !== undefined) selectOption(e, opt);
        } else if (e.key === "Escape") {
            e.preventDefault();
            if (!disableClearable) {
                selectOption(e, null);
            }
            setOpen(false);
        }
    };

    const handleInputFocus = () => {
        if (disabled) return;
        setShowAllOnFocus(true);
        setOpen(true);
    };
    const handleInputBlur = () => {
        // If inputValue doesn't match selected value and clearing allowed, restore to selected label
        if (value && inputValue !== getOptionLabel(value)) {
            setInputValue(getOptionLabel(value));
        }
        if (!open) {
            setShowAllOnFocus(false);
        }
    };

    const handleOptionMouseDown = (e, opt) => {
        if (disabled) return;
        e.preventDefault();
        selectOption(e, opt);
    };

    const listboxId = useMemo(
        () => `ca-list-${Math.random().toString(36).slice(2, 8)}`,
        []
    );

    // Handler for dropdown arrow click
    const handleDropdownArrowMouseDown = (e) => {
        // Handle at mousedown to prevent root onMouseDown from firing
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        setOpen((prev) => !prev);
        setShowAllOnFocus(true);
        if (inputRef.current) inputRef.current.focus();
    };

    // Always open options when clicking anywhere on the input/root (no toggle)
    const handleRootMouseDown = (e) => {
        if (disabled) return;
        // Do not react to default-prevented events (e.g., arrow handler)
        if (e.defaultPrevented) return;
        setShowAllOnFocus(true);
        setOpen(true);
        if (inputRef.current) inputRef.current.focus();
    };

    // Build params for renderInput consistent with how MUI provides hooks for TextField
    const params = useMemo(() => {
        return {
            id: name,
            size: size,
            disabled: disabled,
            InputLabelProps: {
                shrink: open || Boolean(inputValue) || Boolean(value),
            },
            InputProps: {
                ref: anchorRef,
                onMouseDown: handleRootMouseDown,
                endAdornment: (
                    <span 
                        onMouseDown={handleDropdownArrowMouseDown}
                        style={{ 
                            opacity: 0.6, 
                            paddingLeft: 6,
                            cursor: disabled ? 'default' : 'pointer',
                            userSelect: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '20px',
                            minHeight: '20px'
                        }}
                        aria-label={open ? "Close options" : "Open options"}
                        role="button"
                        tabIndex={disabled ? -1 : 0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handleDropdownArrowMouseDown(e);
                            }
                        }}
                    >
                        ▾
                    </span>
                ),
                style: open
                    ? {
                          borderColor: "#d32f2f",
                      }
                    : undefined,
            },
            inputProps: {
                ref: inputRef,
                role: "combobox",
                "aria-expanded": open,
                "aria-disabled": disabled,
                "aria-controls": listboxId,
                "aria-autocomplete": "list",
                autoComplete: "off",
                disabled,
                value: inputValue,
                onChange: handleInputChange,
                onKeyDown: handleKeyDown,
                onFocus: handleInputFocus,
                onBlur: handleInputBlur,
                name,
                placeholder,
            },
        };
    }, [name, size, open, inputValue, value, listboxId, placeholder, disabled]);

    const rootStyle = coerceStyleFromSx(sx);

    return (
        <div
            className={className}
            style={rootStyle}
            {...other}
            ref={containerRef}
        >
            {typeof renderInput === "function" ? renderInput(params) : null}

            {open && filteredOptions.length > 0 && (
                <div
                    className={`hide-scrollbar ${listboxProps.className || ""}`}
                    ref={listboxRef}
                    id={listboxId}
                    role="listbox"
                    style={{
                        position: disablePortal ? "absolute" : "absolute",
                        zIndex: 1300,
                        background: "#fff",
                        border: "1px solid rgba(0,0,0,0.12)",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
                        marginTop: 4,
                        borderRadius: 4,
                        maxHeight: 280,
                        overflowY: "auto",
                        padding: 0,
                        width: containerRef.current
                            ? containerRef.current.getBoundingClientRect().width
                            : undefined,
                        ...(listboxProps.style || {}),
                    }}
                    onMouseDown={(e) => {
                        // Keep input focused; avoid closing when clicking inside the listbox
                        e.preventDefault();
                    }}
                >
                    {filteredOptions.map((opt, idx) => {
                        const selected = multiple
                            ? Array.isArray(value) &&
                              value.some((v) => isOptionEqualToValue(v, opt))
                            : value
                            ? isOptionEqualToValue(opt, value)
                            : false;
                        const highlighted = idx === highlightedIndex;

                        if (typeof renderOption === "function") {
                            const optionProps = {
                                key: `opt-${idx}`,
                                role: "option",
                                "aria-selected": selected,
                                onMouseDown: (e) =>
                                    handleOptionMouseDown(e, opt),
                                onMouseEnter: () => setHighlightedIndex(idx),
                                style: {
                                    listStyle: "none",
                                    listStyleType: "none",
                                },
                            };
                            return renderOption(optionProps, opt, { selected });
                        }

                        return (
                            <div
                                key={`opt-${idx}`}
                                role="option"
                                aria-selected={selected}
                                onMouseDown={(e) =>
                                    handleOptionMouseDown(e, opt)
                                }
                                onMouseEnter={() => setHighlightedIndex(idx)}
                                style={{
                                    padding:
                                        size === "small"
                                            ? "6px 10px"
                                            : "8px 12px",
                                    cursor: "pointer",
                                    background: highlighted
                                        ? "#f5f5f5"
                                        : selected
                                        ? "#f0f7ff"
                                        : "#fff",
                                }}
                            >
                                {getOptionLabel(opt)}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default HodAutocomplete;



